import axios from 'axios';
import { 
  Appointment, 
  AppointmentFormData, 
  LoginFormData, 
  RegisterFormData, 
  AuthResponse,
  User,
  Template,
  TemplateFormData,
  TemplatePreview
} from '../types';
import { safeSetItem, safeGetItem, safeRemoveItem } from '../utils/storageUtils';

// Utiliser la variable d'environnement ou localhost par défaut
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
console.log('API URL utilisée:', API_URL); // Pour déboguer

// Créer une instance axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Service d'authentification
export const authService = {
  // Connexion
  login: async (credentials: LoginFormData): Promise<AuthResponse> => {
    try {
      const response = await api.post('/api/auth/login', credentials);
      
      // Vérifier que la réponse contient un token
      if (response.data && response.data.token) {
        // Utiliser la fonction sécurisée pour stocker le token
        safeSetItem('token', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      throw error;
    }
  },

  // Inscription
  register: async (userData: RegisterFormData): Promise<AuthResponse> => {
    try {
      const response = await api.post('/api/auth/register', userData);
      
      // Vérifier que la réponse contient un token
      if (response.data && response.data.token) {
        // Utiliser la fonction sécurisée pour stocker le token
        safeSetItem('token', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      throw error;
    }
  },

  // Déconnexion
  logout: (): void => {
    safeRemoveItem('token');
  },

  // Vérifier le token
  verifyToken: async (): Promise<User> => {
    const response = await api.get('/api/auth/verify');
    return response.data.user;
  },

  // Récupérer le token du localStorage
  getToken: (): string | null => {
    return safeGetItem<string | null>('token', null);
  },

  // Vérifier si l'utilisateur est authentifié
  isAuthenticated: (): boolean => {
    return !!authService.getToken();
  },
};

// Service de gestion des rendez-vous
export const appointmentService = {
  // Récupérer tous les rendez-vous (avec filtre optionnel)
  getAll: async (status?: string): Promise<Appointment[]> => {
    try {
      let url = '/api/appointments';
      // Si un statut est fourni ET qu'il est différent de 'all', on l'ajoute à l'URL
      if (status && status !== 'all') {
        url += `?status=${encodeURIComponent(status)}`;
      }
      // Si status est 'all' ou non fourni, on appelle juste /api/appointments (qui exclut 'completed' par défaut)

      console.log(`[API Service] Fetching appointments from: ${url}`); // Log pour débugger
      const response = await api.get(url); // Utiliser l'URL construite
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des rendez-vous:', error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Récupérer les rendez-vous du jour
  getToday: async (): Promise<Appointment[]> => {
    try {
      const response = await api.get('/api/appointments/today');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des rendez-vous du jour:', error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Récupérer les rendez-vous à venir
  getUpcoming: async (): Promise<Appointment[]> => {
    try {
      const response = await api.get('/api/appointments/upcoming');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des rendez-vous à venir:', error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Récupérer un rendez-vous par son ID
  getById: async (id: number): Promise<Appointment> => {
    try {
      const response = await api.get(`/api/appointments/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du rendez-vous ${id}:`, error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Créer un nouveau rendez-vous
  create: async (appointment: AppointmentFormData): Promise<Appointment> => {
    try {
      const response = await api.post('/api/appointments', appointment);
      return response.data.appointment;
    } catch (error) {
      console.error('Erreur lors de la création du rendez-vous:', error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Mettre à jour un rendez-vous
  update: async (id: number, appointment: AppointmentFormData): Promise<Appointment> => {
    try {
      const response = await api.put(`/api/appointments/${id}`, appointment);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du rendez-vous ${id}:`, error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Supprimer un rendez-vous
  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/appointments/${id}`);
    } catch (error) {
      console.error(`Erreur lors de la suppression du rendez-vous ${id}:`, error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },

  // Mettre à jour le statut d'un rendez-vous
  updateStatus: async (id: number, status: Appointment['status']): Promise<Appointment> => {
    try {
      const response = await api.patch(`/api/appointments/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut du rendez-vous ${id}:`, error);
      
      // Vérifier si l'erreur est liée à un problème de parsing JSON
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.error('Erreur de parsing JSON:', error);
        throw new Error('Erreur de format des données. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion au serveur
      if (axios.isAxiosError(error) && !error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.');
      }
      
      // Vérifier si l'erreur est liée à un problème de connexion à la base de données
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        throw new Error('La base de données est temporairement indisponible. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  },
};

// Service pour les modèles de communication
export const templateService = {
  getAll: async (): Promise<Template[]> => {
    const response = await api.get('/api/templates');
    return response.data;
  },

  getById: async (id: number): Promise<Template> => {
    const response = await api.get(`/api/templates/${id}`);
    return response.data;
  },

  getByTypeAndCategory: async (type: Template['type'], category: Template['category']): Promise<Template[]> => {
    const response = await api.get(`/api/templates/type/${type}/category/${category}`);
    return response.data;
  },

  create: async (template: TemplateFormData): Promise<Template> => {
    const response = await api.post('/api/templates', template);
    return response.data;
  },

  update: async (id: number, template: TemplateFormData): Promise<Template> => {
    const response = await api.put(`/api/templates/${id}`, template);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/templates/${id}`);
  },

  preview: async (template: TemplateFormData): Promise<TemplatePreview> => {
    const response = await api.post('/api/templates/preview', { template });
    return response.data;
  },
};

// Service pour les autres fonctionnalités
export const servicesAPI = {
  getSMSCredits: async (): Promise<{ credits: number; success?: boolean; error?: string; details?: string }> => {
    try {
      const response = await api.get('/api/services/sms-credits');
      return response.data;
    } catch (error) {
      console.error('Error fetching SMS credits:', error);
      // Si l'erreur contient une réponse du serveur, utiliser ces données
      if (axios.isAxiosError(error) && error.response?.data) {
        return { ...error.response.data, credits: error.response.data.credits || 0 };
      }
      // Sinon, créer une réponse d'erreur par défaut
      return { 
        credits: 0, 
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue lors de la récupération des crédits SMS'
      };
    }
  },
  
  // Vérifier la connexion à l'API SMSFactor
  verifySMSConnection: async (): Promise<{ success: boolean; message: string; credits?: number }> => {
    try {
      const response = await api.get('/api/services/verify-sms-connection');
      return response.data;
    } catch (error) {
      console.error('Error verifying SMS connection:', error);
      // Si l'erreur contient une réponse du serveur, utiliser ces données
      if (axios.isAxiosError(error) && error.response?.data) {
        return error.response.data;
      }
      // Sinon, créer une réponse d'erreur par défaut
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Erreur inconnue lors de la vérification de la connexion SMS'
      };
    }
  },
  
  // Envoyer un SMS de test
  sendTestSMS: async (phoneNumber: string, message?: string, simulate: boolean = false): Promise<any> => {
    try {
      if (simulate) {
        const response = await api.get(`/api/services/simulate?phoneNumber=${encodeURIComponent(phoneNumber)}&message=${encodeURIComponent(message || '')}`);
        return response.data;
      } else {
        const response = await api.post('/api/services/test-sms', { phoneNumber, message });
        return response.data;
      }
    } catch (error) {
      console.error('Error sending test SMS:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.details || error.response.data.error || 'Erreur lors de l\'envoi du SMS de test');
      }
      throw new Error('Erreur lors de l\'envoi du SMS de test');
    }
  },
  
  // Simuler un SMS de rappel de rendez-vous
  simulateReminderSMS: async (appointmentData: { phoneNumber: string, appointmentDate: string, appointmentTime: string, doctor: string }): Promise<any> => {
    try {
      const response = await api.post('/api/services/simulate-reminder', appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error simulating reminder SMS:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.details || error.response.data.error || 'Erreur lors de la simulation du SMS de rappel');
      }
      throw new Error('Erreur lors de la simulation du SMS de rappel');
    }
  },
};

export default api;
